import { useState, useEffect, useCallback } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useRefundRelayByIdQuery } from './__generated__/useRefundRelayByIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const RefundByIdQuery = graphql`
  query useRefundRelayByIdQuery($id: ID!) {
    getRefund(id: $id) {
      id
      amount
      createdOn
      description
      status
      chargeId
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface RefundGraphQL {
  id: string;
  amount?: number | null;
  createdOn?: string | null;
  description?: string | null;
  status?: string | null;
  chargeId?: string | null;
  user?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

// ============================================================================
// Hook
// ============================================================================

export function useRefundByIdQuery(
  refundId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<RefundGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(refundId);

  useEffect(() => {
    if (!enabled || !refundId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useRefundRelayByIdQuery>(
          relayEnvironment,
          RefundByIdQuery,
          { id: refundId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getRefund as RefundGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch refund'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refundId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}
